import BaseComponent from "./base_component_controller"

import { interpolator } from "hb/lib/collection_helpers"
import { useResize } from "hb/insights/use_resize"
import { useChartConfig } from "hb/insights/use_chart_config"
import { dimensionTypes, axisTypes } from "hb/insights/chart"
import { nestedGroupByToMap, nestedGroupBy, foldedMap } from "hb/lib/collection_helpers"

export default class extends BaseComponent {
  static viewId = "scatter"

  static get values() {
    return {
      ...BaseComponent.values,

      querySessionId: String,
      schema: Array,
      results: Array,
      chartConfig: Object
    }
  }

  static configs = [
    "xField",
    "yField",
    {name: "groupField", nullable: true},
    {name: "scaleField", nullable: true},
    {name: "groups", default: "{}", type: "json"},
  ]

  connect() {
    super.connect()

    useChartConfig(this);
  }

  outletsConnected() {
    super.outletsConnected()

    useResize(this, {element: this.outletComponent("chart").chartTarget})
    this.initQuery();
  }

  validate() {
    let {xField, yField, groupField, scaleField} = this.config;

    if(!xField || !yField) {
      this.errorMessage = "Select fields in chart config or update data to fit chart"
    } else if (this.hasUnknownFields(xField, yField, groupField, scaleField)) {
      this.errorMessage = "Unknown field selected"
    }
  }

  processConfig() {
    const { results } = this.query;
    let { yField, groupField, groups } = this.config;

    let colorIdx = -1;
    const gatherColor = (n) => {
      colorIdx = (colorIdx + 1) % this.colorValues.length;
      return groups[n]?.color || this.colorValues[colorIdx];
    }

    let groupConfig;
    if (groupField) {
      const zDomain = Array.from(new Set(results.map(d => d.field(groupField))));
      this.derivedConfig.zDomain = zDomain;
      groupConfig = zDomain.map((name, idx) => ({
        idx, name,
        disabled: this.groupDisabled(idx),
        color: gatherColor(name)
      }))
    } else {
      groupConfig = [{
        idx: 0,
        name: yField,
        color: gatherColor(yField)
      }]
    }

    this.derivedConfig.groupConfig = groupConfig;
  }

  renderConfig() {
    const {meta} = this.query;

    ["xField", "yField", "groupField", "scaleField"].forEach(name => {
      let allFields;
      if(name === "scaleField" || name === "groupField") {
        allFields = ["__empty", ...meta.fields];
      } else {
        allFields = meta.fields;
      }

      this.find(`[data-input-name="${name}"] ul`).innerHTML = allFields.map(field =>
        this.comboboxItemTemplate({
          classes: this[name] === field ? "current" : "",
          value: field,
          id: field,
          display: field,
          ...(field == "__empty" && {
            display: null,
            badge: "none"
          })
        })
      ).join("\n")
    })

    this.find(`input[name="xField"]`).value = this.config.xField || "";
    this.find(`input[name="yField"]`).value = this.config.yField || "";
    this.find(`input[name="groupField"]`).value = this.config.groupField || "";
    this.find(`input[name="scaleField"]`).value = this.config.scaleField || "";
    this.renderFieldGroups(this.derivedConfig.groupConfig)
  }

  render() {
    let {meta, results} = this.query;

    let { xField, yField, groupField, scaleField } = this.config;

    if(!xField || !yField || !results) {
      return
    }

    let scaleInterpolator, dataset, series

    const xFieldType = meta.type(xField);
    const yFieldType = meta.type(yField);

    if(scaleField) {
      scaleInterpolator = interpolator(
        results.map(r => r.field(scaleField)),
        4, 40
      )
    }

    const scatterSeriesDefault = {
      type: "scatter",
      animation: false,
      symbolSize: 5,
    }

    if(groupField) {
      const nested = nestedGroupByToMap(results,
        r => [r.field(xField), r.field(groupField)],
        r => r.field(yField)
      )

      const folded = foldedMap(nested, xField)

      if(scaleField) {
        const nestedScale = nestedGroupBy(results,
          r => [r.field(xField), r.field(groupField)],
          r => r.field(scaleField)
        )

        dataset = {
          source: folded,
          dimensions: [
            {name: xField, type: dimensionTypes[xFieldType]},
            {name: yField, type: dimensionTypes[yFieldType]},
            ...this.derivedConfig.zDomain
          ]
        }

        series = this.derivedConfig.groupConfig
          .filter(({disabled}) => !disabled)
          .map(({name, color}) => ({
            ...scatterSeriesDefault,
            name,
            itemStyle: {color},
            dimensions: [xField, name],
            symbolSize: (data, b) => {
              const [kName, o] = b.dimensionNames
              const k = data[kName]
              return scaleInterpolator(nestedScale[k][o])
            },
          }))

      } else {
        dataset = {
          source: folded,
          dimensions: [
            {name: xField, type: dimensionTypes[xFieldType]},
            {name: yField, type: dimensionTypes[yFieldType]},
            ...this.derivedConfig.zDomain
          ]
        }

        series = this.derivedConfig.groupConfig
          .filter(({disabled}) => !disabled)
          .map(({name, color}) => ({
            ...scatterSeriesDefault,
            name,
            itemStyle: {color},
            dimensions: [xField, name]
          }))
      }
    } else {
      if(scaleField) {
        dataset = {
          dimensions: [xField, yField, scaleField],
          source: results.map(r => r.slice(yField, xField, scaleField))
        }

        series = [{
          ...scatterSeriesDefault,
          name: yField,
          itemStyle: {color: this.derivedConfig.groupConfig[0].color},
          symbolSize: (data) => {
            return scaleInterpolator(data[scaleField])
          },
        }]
      } else {
        dataset = {
          dimensions: [xField, yField],
          source: results.map(r => r.slice(yField, xField))
        }
        series = [{
          ...scatterSeriesDefault,
          itemStyle: {color: this.derivedConfig.groupConfig[0].color},
          name: yField
        }]
      }
    }

    let defaults = this.defaultChartConfig

    if(scaleField) {
      defaults.tooltip.formatter = params => {
        const {data, marker} = params
        let s = "<div>"
        s += `<span class="label">${data[yField]} - ${data[xField]}</span>`
        if(scaleField) {
          s += `<br />${marker} <span>${data[scaleField]}</span>`
        }
        s += "</div>"
        return s
      }
    }

    const options = {
      ...defaults,
      dataset,
      series,
      xAxis: {
        ...defaults.xAxis,
        type: axisTypes[xFieldType],
        name: xField
      },
      yAxis: {
        type: axisTypes[yFieldType],
        name: ""
      }
    }

    this.outletComponent("chart").setChartOptions(options)
    this.outletComponent("legend").render(this.derivedConfig.groupConfig)
  }
}
